import {
  Mountain,
  Building2,
  Bird,
  Shapes,
  Plane,
  Contrast,
  Gem,
  Gamepad2,
} from "lucide-react";

/**
 * Canvas collection themes — shared between the storefront and
 * recommended as the Shopify upload category structure.
 *
 * Shopify upload rule:
 *   Product type → "Canvas Prints"
 *   Theme tag    → one of the slugs below (e.g. "nature-landscape")
 */
export const CANVAS_COLLECTIONS = [
  {
    slug: "nature-landscape",
    title: "Nature & Landscape",
    short: "Mountains, waterfalls, skies, and open views",
    description:
      "Sweeping landscapes, dramatic skies, and serene water scenes. The most popular category for living rooms, bedrooms, and hallways.",
    icon: Mountain,
    image:
      "https://images.pexels.com/photos/29609561/pexels-photo-29609561.jpeg?auto=compress&cs=tinysrgb&w=800",
    keywords: [
      "nature",
      "landscape",
      "mountain",
      "mountains",
      "waterfall",
      "lake",
      "sky",
      "sunset",
      "sunrise",
      "forest",
      "ocean",
      "sea",
      "river",
      "clouds",
      "cliff",
      "scenic",
      "panoramic",
      "shepherd",
      "sheep",
    ],
  },
  {
    slug: "city-architecture",
    title: "City & Architecture",
    short: "Skylines, bridges, landmarks, and urban scenes",
    description:
      "Iconic skylines, dramatic bridges, and architectural photography. Suited for offices, modern apartments, and contemporary spaces.",
    icon: Building2,
    image:
      "https://images.pexels.com/photos/18312319/pexels-photo-18312319.jpeg?auto=compress&cs=tinysrgb&w=800",
    keywords: [
      "city",
      "architecture",
      "skyline",
      "bridge",
      "building",
      "urban",
      "new york",
      "empire",
      "tower",
      "fireworks",
      "night",
    ],
  },
  {
    slug: "animals-wildlife",
    title: "Animals & Wildlife",
    short: "Horses, wildlife, ocean life, and nature's creatures",
    description:
      "Powerful wildlife shots, graceful animals, and vibrant underwater scenes. Statement pieces that bring energy to any room.",
    icon: Bird,
    image:
      "https://images.unsplash.com/photo-1474511320723-9a56873867b5?w=800&q=80",
    keywords: [
      "animal",
      "animals",
      "wildlife",
      "horse",
      "lion",
      "tiger",
      "fish",
      "bird",
      "dog",
      "cat",
      "elephant",
      "underwater",
      "coral",
      "wild",
      "spirit",
    ],
  },
  {
    slug: "abstract-modern",
    title: "Abstract & Modern",
    short: "Bold colour, geometric form, and contemporary art",
    description:
      "Vibrant abstracts, neon art, and contemporary compositions that add personality. Great for feature walls and creative interiors.",
    icon: Shapes,
    image:
      "https://images.pexels.com/photos/3216997/pexels-photo-3216997.jpeg?auto=compress&cs=tinysrgb&w=800",
    keywords: [
      "abstract",
      "modern",
      "neon",
      "futuristic",
      "geometric",
      "contemporary",
      "vibrant",
      "colourful",
      "colorful",
      "motion",
      "supercar",
    ],
  },
  {
    slug: "travel-places",
    title: "Travel & Places",
    short: "World landmarks, desert vistas, and far-away scenes",
    description:
      "Pyramids, temples, coastal villages, and iconic destinations. For the well-travelled or the dreamer.",
    icon: Plane,
    image:
      "https://images.unsplash.com/photo-1551857704-ba9b620ad444?w=800&q=80",
    keywords: [
      "travel",
      "place",
      "places",
      "destination",
      "pyramid",
      "pyramids",
      "desert",
      "temple",
      "landmark",
      "jet",
      "flying",
      "flight",
      "explorer",
    ],
  },
  {
    slug: "black-white",
    title: "Black & White",
    short: "Monochrome, contrast, and timeless composition",
    description:
      "Stripped-back monochrome art that suits any room. Timeless, versatile, and always elegant.",
    icon: Contrast,
    image:
      "https://images.pexels.com/photos/9144655/pexels-photo-9144655.jpeg?auto=compress&cs=tinysrgb&w=800",
    keywords: [
      "black",
      "white",
      "monochrome",
      "mono",
      "b&w",
      "bw",
      "greyscale",
      "grayscale",
      "noir",
    ],
  },
  {
    slug: "luxury-statement",
    title: "Luxury & Statement",
    short: "Gold-tone, oversized, and high-impact pieces",
    description:
      "Pieces designed to command attention. Metallic tones, oversized formats, and designs that anchor a room.",
    icon: Gem,
    image:
      "https://images.pexels.com/photos/13490219/pexels-photo-13490219.jpeg?auto=compress&cs=tinysrgb&w=800",
    keywords: [
      "luxury",
      "statement",
      "gold",
      "premium",
      "elegant",
      "oversized",
      "dramatic",
      "majestic",
    ],
  },
  {
    slug: "kids-playful",
    title: "Kids & Playful",
    short: "Fun, colourful, and imaginative wall art",
    description:
      "Bright, cheerful prints for children's rooms, playrooms, and family spaces. From cartoon animals to space adventures.",
    icon: Gamepad2,
    image:
      "https://images.pexels.com/photos/7055868/pexels-photo-7055868.jpeg?auto=compress&cs=tinysrgb&w=800",
    keywords: [
      "kids",
      "playful",
      "children",
      "nursery",
      "cartoon",
      "fun",
      "cute",
      "baby",
      "toddler",
    ],
  },
];

const CANVAS_COLLECTION_SLUGS = new Set(
  CANVAS_COLLECTIONS.map((collection) => collection.slug)
);

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function normalizeTags(tags) {
  if (Array.isArray(tags)) return tags;
  if (typeof tags === "string") {
    return tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
  }
  return [];
}

function keywordMatches(keyword, haystack) {
  const normalizedKeyword = keyword.toLowerCase().trim();
  if (!normalizedKeyword) return false;

  if (/^[a-z0-9-]+$/.test(normalizedKeyword)) {
    const pattern = new RegExp(`\\b${escapeRegExp(normalizedKeyword)}\\b`, "i");
    return pattern.test(haystack);
  }

  return haystack.includes(normalizedKeyword);
}

function scoreCollection(product, collection) {
  const title = (product.title || "").toLowerCase();
  const description = (product.description || "").toLowerCase();
  const tags = normalizeTags(product.tags).map((tag) => tag.toLowerCase());

  return collection.keywords.reduce((score, keyword) => {
    let keywordScore = 0;
    if (tags.some((tag) => keywordMatches(keyword, tag))) keywordScore += 4;
    if (keywordMatches(keyword, title)) keywordScore += 3;
    if (keywordMatches(keyword, description)) keywordScore += 1;
    return score + keywordScore;
  }, 0);
}

/**
 * Classify a canvas product into all matching collection slugs.
 */
export function classifyCanvasAll(product) {
  const tags = normalizeTags(product.tags).map((tag) => tag.toLowerCase());
  const explicitMatches = [...new Set(tags.filter((tag) => CANVAS_COLLECTION_SLUGS.has(tag)))];

  if (explicitMatches.length > 0) {
    return explicitMatches;
  }

  const rankedMatches = CANVAS_COLLECTIONS.map((collection) => ({
    slug: collection.slug,
    score: scoreCollection(product, collection),
  }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score);

  if (rankedMatches.length === 0) {
    return [];
  }

  const topScore = rankedMatches[0].score;
  const secondScore = rankedMatches[1]?.score ?? 0;

  if (topScore >= 3 || topScore > secondScore) {
    return [rankedMatches[0].slug];
  }

  return rankedMatches
    .filter((entry) => entry.score === topScore)
    .map((entry) => entry.slug);
}
